import { useRouter } from 'next/router'

import {
  useMutationCreateBooking,
  useMutationUpdateBooking,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingPayload, type ParameterID } from '@flecto/types'
import { useNotifications } from '@flecto/ui'

import { useInsuranceStatus } from '../../booking-insurances/hooks/useInsuranceStatus'

export type UseUpdateBookingDatesOptions = {
  bookingId?: ParameterID
}

export type HandleBookingDatesPayload = {
  start_at?: string
  end_at?: string
  force_suggested_booking_cost?: boolean
}

export function useUpdateBookingDates({
  bookingId,
}: UseUpdateBookingDatesOptions) {
  const router = useRouter()
  const { t } = useTranslation('saas', { keyPrefix: 'booking_dates' })

  const { showNotification } = useNotifications()

  const { mutateAsync: updateBooking, isLoading: isUpdating } =
    useMutationUpdateBooking()

  const { mutateAsync: createBooking, isLoading: isCreating } =
    useMutationCreateBooking()

  const { mutate } = useInsuranceStatus({ bookingId })

  const handleUpdateBookingDates = async ({
    start_at,
    end_at,
    force_suggested_booking_cost,
  }: HandleBookingDatesPayload) => {
    const bookingPayload: CoreBookingPayload = {
      start_at,
      end_at,
    }

    try {
      await updateBooking({
        bookingId,
        booking: bookingPayload,
        options: {
          force_suggested_booking_cost,
        },
      })

      mutate()

      showNotification({
        type: 'success',
        message: t('notification_update_booking_dates_success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_update_booking_dates_error'),
      })
    }
  }

  const handleCreateBookingWithDates = async ({
    start_at,
    end_at,
  }: HandleBookingDatesPayload) => {
    const bookingPayload: CoreBookingPayload = {
      start_at,
      end_at,
    }

    try {
      const booking = await createBooking({
        booking: bookingPayload,
      })

      showNotification({
        type: 'success',
        message: t('notification_update_booking_dates_success'),
      })

      router.push(`/bookings/${booking.internal_id}`)
      return
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_update_booking_dates_error'),
      })
    }
  }

  return {
    handleUpdateBookingDates,
    handleCreateBookingWithDates,
    isLoading: isCreating || isUpdating,
  }
}
